import * as React from "react"
import { ZoneConnectionType } from "@/types"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Boolean } from "aws-sdk/clients/codepipeline"
import { useForm } from "react-hook-form"
import * as z from "zod"

import { validateUserDeletePermission } from "@/lib/api/auth"
import { deleteZoneConnectionRate } from "@/lib/api/zone-connection-rates"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import {
  Credenza,
  CredenzaBody,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
} from "@/components/ui/credenza"
import { Dialog } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import ZoneConnectionRatesForm from "@/components/forms/zone-connection-rates-form"
import { Header } from "@/components/header"
import { Icons } from "@/components/icons"

interface DataProps {
  data: ZoneConnectionType
}

export function ZoneConnectionRatesPage({ data }: DataProps) {
  const [showDropDown, setShowDropDown] = React.useState<boolean>(false)
  const [showZoneConnectionRatesModal, setShowZoneConnectionRatesModal] =
    React.useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false)

  return (
    <div>
      <DropdownMenu open={showDropDown} onOpenChange={setShowDropDown}>
        <DropdownMenuTrigger>
          <div className="flex h-8 w-8 items-center justify-center rounded-md border transition-colors hover:bg-muted">
            <Icons.ellipsis className="size-4" />
            <span className="sr-only">Open</span>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => {
              setShowDropDown(false)
              setShowZoneConnectionRatesModal(!showZoneConnectionRatesModal)
            }}
          >
            <Icons.edit className="mr-2 size-4" />
            Edit Zone Connection Rates
          </DropdownMenuItem>

          <DropdownMenuItem
            className="text-destructive"
            onSelect={() => {
              setShowDeleteModal(true)
              setShowDropDown(false)
            }}
          >
            <Icons.delete className="mr-2 size-4" />
            Delete Zone Connection Rates
          </DropdownMenuItem>
        </DropdownMenuContent>

        <HandleDelete
          zoneConnectionRateId={data.id}
          showDeleteModal={showDeleteModal}
          setShowDeleteModal={setShowDeleteModal}
        />
        <EditZoneConnectionRateModal
          editData={data}
          showZoneConnectionRatesModal={showZoneConnectionRatesModal}
          setShowZoneConnectionRatesModal={setShowZoneConnectionRatesModal}
        />
      </DropdownMenu>
    </div>
  )
}

function EditZoneConnectionRateModal({
  editData,
  showZoneConnectionRatesModal,
  setShowZoneConnectionRatesModal,
}: {
  editData: ZoneConnectionType
  showZoneConnectionRatesModal: boolean
  setShowZoneConnectionRatesModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  return (
    <Credenza
      open={showZoneConnectionRatesModal}
      onOpenChange={setShowZoneConnectionRatesModal}
    >
      <CredenzaContent className="flex w-full max-w-[900px] justify-center">
        <CredenzaBody>
          <Header
            subroutes={[
              {
                route: "/zone-settings/zone-connection-rates",
                label: "Zone connection rates",
              },
            ]}
          >
            Edit Zone Connection Rate
          </Header>
          <ZoneConnectionRatesForm
            setShowZoneConnectionRatesModal={setShowZoneConnectionRatesModal}
            editData={editData}
          />
        </CredenzaBody>
      </CredenzaContent>
    </Credenza>
  )
}

function HandleDelete({
  zoneConnectionRateId,
  showDeleteModal,
  setShowDeleteModal,
}: {
  zoneConnectionRateId: number
  showDeleteModal: boolean
  setShowDeleteModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const formSchema = z.object({
    id: z.coerce
      .number()
      .min(1, "Please enter the id below to delete")
      .refine((val) => val === zoneConnectionRateId, {
        message: "Input ID must match the Zone Connection Rate ID.",
      }),
  })
  const form = useForm({
    resolver: zodResolver(formSchema),
  })

  const mutation = useMutation({
    mutationFn: async () => {
      return await deleteZoneConnectionRate(zoneConnectionRateId)
    },
    onSuccess: () => {
      toast({
        description: "Zone connection rate deleted successfully",
      })
      setShowDeleteModal(!showDeleteModal)
      queryClient.invalidateQueries({ queryKey: ["connection-rates"] })
    },
  })

  function handleDelete() {
    if (!validateUserDeletePermission()) return
    mutation.mutate()
  }

  return (
    <Credenza open={showDeleteModal} onOpenChange={setShowDeleteModal}>
      <CredenzaContent>
        <Form {...form}>
          <form
            className="space-y-4 pb-4"
            onSubmit={form.handleSubmit(handleDelete)}
          >
            <CredenzaHeader>
              <CredenzaTitle>Delete Zone Connection Rate</CredenzaTitle>
              <CredenzaDescription>
                You cannot undo this action!
              </CredenzaDescription>
            </CredenzaHeader>
            <CredenzaBody>
              <FormField
                control={form.control}
                name="id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Type "{zoneConnectionRateId}" to delete.
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value ?? ""} // Allow empty input
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === ""
                              ? undefined
                              : Number(e.target.value),
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CredenzaBody>
            <CredenzaFooter>
              <CredenzaClose asChild>
                <Button variant={"outline"}>Cancel</Button>
              </CredenzaClose>
              <Button
                disabled={mutation.isPending}
                type="submit"
                variant={"destructive"}
              >
                {mutation.isPending && (
                  <Icons.loader className="mr-2 animate-spin" />
                )}
                Delete
              </Button>
            </CredenzaFooter>
          </form>
        </Form>
      </CredenzaContent>
    </Credenza>
  )
}
